import React, { useMemo } from 'react';
import type { InventoryItem, Order, Recipe } from '../../types';
import TrendingUpIcon from '../icons/TrendingUpIcon';
import CalendarIcon from '../icons/CalendarIcon';
import InventoryIcon from '../icons/InventoryIcon';
import ShoppingBagIcon from '../icons/ShoppingBagIcon';
import ClipboardCheckIcon from '../icons/ClipboardCheckIcon';
import CheckCircleIcon from '../icons/CheckCircleIcon';
import ZapIcon from '../icons/ZapIcon';

interface InventoryDashboardProps {
  inventoryItems: InventoryItem[];
  orders: Order[];
  recipes: Recipe[];
}

const InfoCard: React.FC<{ title: string; icon: React.FC<any>; children: React.ReactNode }> = ({ title, icon: Icon, children }) => (
    <div className="bg-white shadow rounded-lg p-6">
        <div className="flex items-center mb-4">
            <Icon className="w-6 h-6 mr-3 text-pink-500" />
            <h3 className="text-lg font-bold text-gray-800">{title}</h3>
        </div>
        {children}
    </div>
);

const StatCard: React.FC<{ 
  title: string; 
  value: string | number; 
  subtitle?: string; 
  icon: React.FC<any>; 
  color: string;
  bgColor: string;
}> = ({ title, value, subtitle, icon: Icon, color, bgColor }) => (
    <div className={`${bgColor} rounded-lg p-6 shadow-md`}>
        <div className="flex items-center justify-between">
            <div>
                <p className="text-sm font-medium text-gray-600 mb-1">{title}</p>
                <p className={`text-3xl font-bold ${color}`}>{value}</p>
                {subtitle && <p className="text-xs text-gray-500 mt-1">{subtitle}</p>}
            </div>
            <Icon className={`w-12 h-12 ${color} opacity-20`} />
        </div>
    </div>
);

const InventoryDashboard: React.FC<InventoryDashboardProps> = ({ inventoryItems, orders, recipes }) => {
    // Safety checks for data
    const safeInventoryItems = inventoryItems || [];
    const safeOrders = orders || [];
    const safeRecipes = recipes || [];

    const analytics = useMemo(() => {
        const todayStart = new Date();
        todayStart.setHours(0, 0, 0, 0);
        const todaysOrders = safeOrders.filter(o => o && o.timestamp >= todayStart.getTime());

        const usageCount: { [key: number]: number } = {};
        todaysOrders.forEach(order => {
            if (order && order.items && Array.isArray(order.items)) {
                order.items.forEach(item => {
                    if (item && item.id) {
                        const recipe = safeRecipes.find(r => r && r.menuItemId === item.id);
                        if (recipe && recipe.ingredients && Array.isArray(recipe.ingredients)) {
                            recipe.ingredients.forEach(ing => {
                                if (ing && ing.inventoryItemId) {
                                    usageCount[ing.inventoryItemId] = (usageCount[ing.inventoryItemId] || 0) + (ing.quantity * item.quantity);
                                }
                            });
                        }
                    }
                });
            }
        });

        const fastMovers = Object.entries(usageCount)
            .map(([id, count]) => ({ item: safeInventoryItems.find(i => i && i.id === parseInt(id)), count }))
            .filter(i => i.item)
            .sort((a, b) => b.count - a.count)
            .slice(0, 5);
        
        const now = Date.now();
        const thirtyDaysAgo = now - 30 * 24 * 60 * 60 * 1000;
        const datedProducts = safeInventoryItems
            .filter(item => item && item.lastUpdated < thirtyDaysAgo)
            .sort((a, b) => a.lastUpdated - b.lastUpdated)
            .slice(0, 5);

        // Calculate statistics
        let outOfStockCount = 0;
        let lowStockCount = 0;
        let inStockCount = 0;
        let totalBulkItems = 0;
        let totalBulkQuantity = 0;

        safeInventoryItems.forEach(item => {
            if (!item) return;
            let availableQuantity = item.stock;
            if (item.isBulkPurchase && item.bulkPurchases && item.bulkPurchases.length > 0) {
                totalBulkItems++;
                const bulkQty = item.bulkPurchases.reduce((sum, bp) => sum + (bp?.remainingQuantity || 0), 0);
                totalBulkQuantity += bulkQty;
                availableQuantity = bulkQty;
            }
            
            if (availableQuantity <= 0) {
                outOfStockCount++;
            } else if (availableQuantity <= item.lowStockThreshold) {
                lowStockCount++;
            } else {
                inStockCount++;
            }
        });

        // For bulk items, check total bulk quantity; for regular items, check stock
        const lowStockItems = safeInventoryItems.filter(item => {
            if (!item) return false;
            let availableQuantity = item.stock;
            if (item.isBulkPurchase && item.bulkPurchases && item.bulkPurchases.length > 0) {
                // Sum all remaining bulk quantities for bulk purchase items
                availableQuantity = item.bulkPurchases.reduce((sum, bp) => sum + (bp.remainingQuantity || 0), 0);
            }
            return availableQuantity <= item.lowStockThreshold;
        });

        // Bulk purchase items
        const bulkPurchaseItems = safeInventoryItems.filter(item => item && item.isBulkPurchase && item.bulkPurchases && item.bulkPurchases.length > 0);
        const bulkPurchasesNeedingAttention = bulkPurchaseItems
            .map(item => {
                const activePurchases = item.bulkPurchases!.filter(bp => bp && bp.remainingQuantity > 0);
                const weeksRemaining = activePurchases.map(bp => ({
                    item,
                    purchase: bp,
                    weeks: bp.weeklyAllocation > 0 ? Math.floor(bp.remainingQuantity / bp.weeklyAllocation) : 0,
                }));
                return weeksRemaining;
            })
            .flat()
            .filter(w => w.weeks < 4) // Less than 4 weeks remaining
            .sort((a, b) => a.weeks - b.weeks);

        // Expiry date alerts
        const expiringItems = safeInventoryItems
            .filter(item => {
                if (!item.hasExpiryDate) return false;
                if (item.expiryDate) {
                    const daysUntilExpiry = Math.floor((item.expiryDate - now) / (24 * 60 * 60 * 1000));
                    return daysUntilExpiry <= 30 && daysUntilExpiry >= 0; // Expiring within 30 days
                }
                if (item.expiryDates && item.expiryDates.length > 0) {
                    return item.expiryDates.some(ed => {
                        const daysUntilExpiry = Math.floor((ed.expiryDate - now) / (24 * 60 * 60 * 1000));
                        return daysUntilExpiry <= 30 && daysUntilExpiry >= 0;
                    });
                }
                return false;
            })
            .map(item => {
                let nearestExpiry: number | null = null;
                if (item.expiryDate) {
                    nearestExpiry = item.expiryDate;
                } else if (item.expiryDates && item.expiryDates.length > 0) {
                    const futureExpiries = item.expiryDates
                        .map(ed => ed.expiryDate)
                        .filter(ed => ed > now)
                        .sort((a, b) => a - b);
                    if (futureExpiries.length > 0) {
                        nearestExpiry = futureExpiries[0];
                    }
                }
                return { item, expiryDate: nearestExpiry };
            })
            .filter(e => e.expiryDate !== null)
            .sort((a, b) => (a.expiryDate || 0) - (b.expiryDate || 0))
            .slice(0, 10);

        return { 
            fastMovers, 
            datedProducts, 
            lowStockItems, 
            bulkPurchasesNeedingAttention, 
            expiringItems,
            stats: {
                totalItems: safeInventoryItems.length,
                outOfStockCount,
                lowStockCount,
                inStockCount,
                totalBulkItems,
                totalBulkQuantity,
                expiringCount: expiringItems.length
            }
        };
    }, [safeInventoryItems, safeOrders, safeRecipes]);
    
    // Early return if no data to prevent rendering issues
    if (!safeInventoryItems || safeInventoryItems.length === 0) {
        return (
            <div className="bg-white shadow rounded-lg p-6">
                <p className="text-gray-500 text-center">No inventory items available. Add items to see dashboard analytics.</p>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Summary Statistics Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <StatCard
                    title="Total Items"
                    value={analytics.stats.totalItems}
                    subtitle="Inventory items"
                    icon={InventoryIcon}
                    color="text-blue-600"
                    bgColor="bg-blue-50"
                />
                <StatCard
                    title="In Stock"
                    value={analytics.stats.inStockCount}
                    subtitle={`${analytics.stats.totalItems > 0 ? Math.round((analytics.stats.inStockCount / analytics.stats.totalItems) * 100) : 0}% of total`}
                    icon={CheckCircleIcon}
                    color="text-green-600"
                    bgColor="bg-green-50"
                />
                <StatCard
                    title="Low Stock"
                    value={analytics.stats.lowStockCount}
                    subtitle="Needs attention"
                    icon={ZapIcon}
                    color="text-yellow-600"
                    bgColor="bg-yellow-50"
                />
                <StatCard
                    title="Out of Stock"
                    value={analytics.stats.outOfStockCount}
                    subtitle="Urgent restock needed"
                    icon={ClipboardCheckIcon}
                    color="text-red-600"
                    bgColor="bg-red-50"
                />
            </div>

            {/* Additional Stats Row */}
            {(analytics.stats.totalBulkItems > 0 || analytics.stats.expiringCount > 0) && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {analytics.stats.totalBulkItems > 0 && (
                        <StatCard
                            title="Bulk Purchase Items"
                            value={analytics.stats.totalBulkItems}
                            subtitle={`${analytics.stats.totalBulkQuantity.toFixed(0)} total units in bulk`}
                            icon={ShoppingBagIcon}
                            color="text-purple-600"
                            bgColor="bg-purple-50"
                        />
                    )}
                    {analytics.stats.expiringCount > 0 && (
                        <StatCard
                            title="Expiring Soon"
                            value={analytics.stats.expiringCount}
                            subtitle="Within 30 days"
                            icon={CalendarIcon}
                            color="text-orange-600"
                            bgColor="bg-orange-50"
                        />
                    )}
                </div>
            )}

            {/* Detailed Information Cards */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2 space-y-6">
                <InfoCard title="Fast Movers (Today)" icon={TrendingUpIcon}>
                     {analytics.fastMovers.length > 0 ? (
                        <ul className="space-y-2 text-sm">
                            {analytics.fastMovers.map(({ item, count }) => (
                                <li key={item!.id} className="flex justify-between">
                                    <span className="text-gray-700">{item!.name}</span>
                                    <span className="font-semibold text-gray-800">{count.toFixed(2)} {item!.unit} used</span>
                                </li>
                            ))}
                        </ul>
                    ) : <p className="text-gray-500 text-sm">No sales recorded yet today to determine fast movers.</p>}
                </InfoCard>

                {analytics.expiringItems.length > 0 && (
                    <InfoCard title="⚠️ Expiring Soon (≤30 days)" icon={CalendarIcon}>
                        <ul className="space-y-2 text-sm">
                            {analytics.expiringItems.map(({ item, expiryDate }) => {
                                if (!expiryDate) return null;
                                const daysUntilExpiry = Math.floor((expiryDate - Date.now()) / (24 * 60 * 60 * 1000));
                                const isUrgent = daysUntilExpiry <= 7;
                                return (
                                    <li key={item.id} className={`flex justify-between p-2 rounded ${isUrgent ? 'bg-red-50 border border-red-200' : 'bg-yellow-50 border border-yellow-200'}`}>
                                        <span className="text-gray-700 font-medium">{item.name}</span>
                                        <span className={`font-semibold ${isUrgent ? 'text-red-700' : 'text-yellow-700'}`}>
                                            {daysUntilExpiry === 0 ? 'Expires today!' : daysUntilExpiry === 1 ? 'Expires tomorrow' : `${daysUntilExpiry} days`}
                                        </span>
                                    </li>
                                );
                            })}
                        </ul>
                    </InfoCard>
                )}

                {analytics.bulkPurchasesNeedingAttention.length > 0 && (
                    <InfoCard title="📦 Bulk Purchases - Low Stock" icon={TrendingUpIcon}>
                        <ul className="space-y-2 text-sm">
                            {analytics.bulkPurchasesNeedingAttention.map(({ item, purchase, weeks }) => (
                                <li key={`${item.id}-${purchase.id}`} className="flex justify-between p-2 rounded bg-blue-50 border border-blue-200">
                                    <div>
                                        <span className="text-gray-700 font-medium">{item.name}</span>
                                        <p className="text-xs text-gray-500">
                                            {purchase.remainingQuantity.toFixed(2)} {item.unit} remaining
                                        </p>
                                    </div>
                                    <span className={`font-semibold ${weeks < 2 ? 'text-red-700' : 'text-blue-700'}`}>
                                        ~{weeks} weeks left
                                    </span>
                                </li>
                            ))}
                        </ul>
                    </InfoCard>
                )}

                <InfoCard title="Dated Products (>30 days)" icon={CalendarIcon}>
                    {analytics.datedProducts.length > 0 ? (
                        <ul className="space-y-2 text-sm">
                            {analytics.datedProducts.map(item => (
                                <li key={item.id} className="flex justify-between">
                                    <span className="text-gray-700">{item.name}</span>
                                    <span className="text-gray-500">Last used: {new Date(item.lastUpdated).toLocaleDateString()}</span>
                                </li>
                            ))}
                        </ul>
                    ) : <p className="text-gray-500 text-sm">All products have been used recently. Great job!</p>}
                </InfoCard>
                </div>

                {/* Right Sidebar - Low Stock Alerts */}
                <div className="space-y-6">
                    {analytics.lowStockItems.length > 0 && (
                        <InfoCard title="🔴 Low Stock Alerts" icon={ZapIcon}>
                            <ul className="space-y-2 text-sm max-h-96 overflow-y-auto">
                                {analytics.lowStockItems.slice(0, 10).map(item => {
                                    if (!item) return null;
                                    let availableQuantity = item.stock;
                                    if (item.isBulkPurchase && item.bulkPurchases && item.bulkPurchases.length > 0) {
                                        availableQuantity = item.bulkPurchases.reduce((sum, bp) => sum + (bp?.remainingQuantity || 0), 0);
                                    }
                                    const isOutOfStock = availableQuantity <= 0;
                                    return (
                                        <li key={item.id} className={`p-2 rounded ${isOutOfStock ? 'bg-red-100 border border-red-300' : 'bg-yellow-50 border border-yellow-200'}`}>
                                            <div className="flex justify-between items-start">
                                                <div className="flex-1">
                                                    <span className="font-medium text-gray-800">{item.name}</span>
                                                    <p className="text-xs text-gray-600 mt-1">
                                                        Stock: {availableQuantity.toFixed(2)} {item.unit} / Threshold: {item.lowStockThreshold} {item.unit}
                                                    </p>
                                                </div>
                                                <span className={`text-xs font-semibold ml-2 ${isOutOfStock ? 'text-red-700' : 'text-yellow-700'}`}>
                                                    {isOutOfStock ? 'OUT' : 'LOW'}
                                                </span>
                                            </div>
                                        </li>
                                    );
                                })}
                            </ul>
                            {analytics.lowStockItems.length > 10 && (
                                <p className="text-xs text-gray-500 mt-2 text-center">
                                    +{analytics.lowStockItems.length - 10} more items
                                </p>
                            )}
                        </InfoCard>
                    )}
                </div>
            </div>
        </div>
    );
};

export default InventoryDashboard;
